export default class RatioProcessor {
    constructor() {
        this.stockNominals = new Map();
    }

    async calculateFields(stockCode, messageData) {
        if (!messageData.lastPrice) {
            return messageData;
        }

        const enrichedData = { ...messageData };
        const lastPrice = messageData.lastPrice;

        try {
            let nominals = this.stockNominals.get(stockCode);

            const { hasNominals, nominals: newNominals } =
                await this.extractNominalsIfExists(messageData);
            if (hasNominals) {
                nominals = newNominals;
                this.stockNominals.set(stockCode, nominals);
            }

            if (!nominals) {
                return enrichedData;
            }

            await Promise.all([
                this.calculateMarketRatios(enrichedData, nominals, lastPrice),
                this.calculateReturnRatios(enrichedData, nominals, lastPrice),
                this.calculateValuationRatios(enrichedData, nominals, lastPrice),
            ]);
        } catch (error) {
            console.warn(`Calculation error for stock ${stockCode}:`, error);
        }

        return enrichedData;
    }

    async calculateMarketRatios(enrichedData, nominals, lastPrice) {
        await new Promise(resolve => setTimeout(resolve, 0));

        if ('pbvNominal' in nominals && nominals.pbvNominal > 0) {
            enrichedData.pbv = nominals.pbvNominal * lastPrice;
        }

        if ('peNominal' in nominals && nominals.peNominal > 0) {
            enrichedData.pe = lastPrice / nominals.peNominal;
        }

        if ('pSalesNominal' in nominals && nominals.pSalesNominal > 0) {
            enrichedData.pSales = nominals.pSalesNominal * lastPrice;
        }

        if ('dividendYieldNominal' in nominals && nominals.dividendYieldNominal > 0) {
            enrichedData.dividendYield = (nominals.dividendYieldNominal / lastPrice) * 100.0;
        }

        if ('pheNominal' in nominals && nominals.pheNominal > 0) {
            enrichedData.phe = lastPrice / nominals.pheNominal;
        }
    }

    async calculateReturnRatios(enrichedData, nominals, lastPrice) {
        await new Promise(resolve => setTimeout(resolve, 0));

        if ('dailyPrevClose' in nominals && nominals.dailyPrevClose > 0) {
            enrichedData.dailyChange = (lastPrice / nominals.dailyPrevClose - 1.0) * 100.0;
            enrichedData.dailyChangePrice = lastPrice - nominals.dailyPrevClose;
        }

        const rocFields = [
            { nominal: 'rocPrevWeekNominal', result: 'rocPrevWeek' },
            { nominal: 'rocMonthToDateNominal', result: 'monthlyChange' },
            { nominal: 'rocPrevMonthNominal', result: 'rocPrevMonth' },
            { nominal: 'rocPrev3MonthsNominal', result: 'rocPrev3Month' },
            { nominal: 'rocPrev6MonthsNominal', result: 'rocPrev6Month' },
            { nominal: 'rocPrevYearNominal', result: 'rocPrevYear' },
            { nominal: 'rocPrev2YearsNominal', result: 'rocPrev2Year' },
            { nominal: 'rocPrev3YearsNominal', result: 'rocPrev3Year' },
            { nominal: 'rocPrev5YearsNominal', result: 'rocPrev5Year' },
            { nominal: 'rocPrev10YearsNominal', result: 'rocPrev10Year' },
            { nominal: 'rocEofPrevYearNominal', result: 'rocEOFPrevYear' },
        ];

        for (const field of rocFields) {
            if (field.nominal in nominals && nominals[field.nominal] !== 0) {
                enrichedData[field.result] = (lastPrice / nominals[field.nominal] - 1.0) * 100.0;
            }
        }
    }

    async calculateValuationRatios(enrichedData, nominals, lastPrice) {
        await new Promise(resolve => setTimeout(resolve, 0));

        if ('marketCapNominal' in nominals && nominals.marketCapNominal > 0) {
            enrichedData.marketCap = nominals.marketCapNominal * lastPrice;
        }

        if ('forwardEPS' in nominals && nominals.forwardEPS > 0) {
            enrichedData.forwardPE = lastPrice / nominals.forwardEPS;
        }

        if ('heps' in nominals && nominals.heps > 0) {
            enrichedData.earningsYieldHE = (nominals.heps / lastPrice) * 100.0;
        }

        if ('epsBasic' in nominals && nominals.epsBasic > 0) {
            enrichedData.earningsYield = (nominals.epsBasic / lastPrice) * 100.0;
        }

        if (
            'netSales' in nominals &&
            'enterpriseValueNominal' in nominals &&
            'securityClassCoefficient' in nominals &&
            'sharesInIssue' in nominals
        ) {
            if (
                nominals.netSales > 0 &&
                nominals.securityClassCoefficient > 0 &&
                nominals.sharesInIssue > 0
            ) {
                enrichedData.evSales = (
                    lastPrice * nominals.sharesInIssue / (nominals.securityClassCoefficient / 100) -
                    nominals.enterpriseValueNominal
                ) / nominals.netSales;
            }
        }
    }

    async extractNominalsIfExists(messageData) {
        const safeNominals = {};
        let foundAnyNominal = false;

        const nominalFields = [
            'pbvNominal',
            'peNominal',
            'pSalesNominal',
            'dividendYieldNominal',
            'pheNominal',
            'rocPrevMonthNominal',
            'rocPrevWeekNominal',
            'rocPrevYearNominal',
            'rocPrev3YearsNominal',
            'rocPrev5YearsNominal',
            'rocPrev10YearsNominal',
            'rocEofPrevYearNominal',
            'marketCapNominal',
            'forwardPENominal',
            'dailyPrevClose',
            'heps',
            'epsBasic',
            'netSales',
            'enterpriseValueNominal',
            'securityClassCoefficient',
            'sharesInIssue',
            'forwardEPS',
            'rocPrev6MonthsNominal',
            'rocPrev3MonthsNominal',
            'rocPrev2YearsNominal',
            'rocMonthToDateNominal',
        ];

        for (let i = 0; i < nominalFields.length; i += 1) {
            if (i % 10 === 0) {
                await new Promise(resolve => setTimeout(resolve, 0));
            }

            const field = nominalFields[i];
            if (field in messageData) {
                const value = this.getSafeNumber(messageData[field]);
                if (value !== 0) {
                    foundAnyNominal = true;
                    safeNominals[field] = value;
                }
            }
        }

        return {
            hasNominals: foundAnyNominal,
            nominals: safeNominals,
        };
    }

    getSafeNumber(value) {
        if (value === null || value === undefined || Number.isNaN(Number(value))) {
            return 0;
        }
        return Number(value);
    }
}
